import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { FILTER_SECTIONS as DEFAULT_SECTIONS } from '@/utils/filterConstants';
import type { ActiveFilters, FilterSectionId, FilterSection } from '@/types/filters';

import CloseIcon from '@/assets/images/svg/close_red_icon.svg';

interface FilterPopupProps {
  isOpen: boolean;
  onClose: () => void;
  activeFilters: ActiveFilters;
  onToggleFilter: (section: FilterSectionId, id: string) => void;
  onClearAll: () => void;
  className?: string;
  /**
   * Optional configuration for filter sections.
   * If not provided, defaults to the static FILTER_SECTIONS constant.
   */
  sections?: FilterSection[];
}

const CheckedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='20'
    height='20'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect x='1' y='1' width='14' height='14' rx='3' fill='#ABF56E' stroke='#BFB0F8' />
    <path
      d='M5 8.5L7 10.5L11 6.5'
      stroke='#1A1036'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const UncheckedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='20'
    height='20'
    viewBox='0 0 16 16'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect x='1' y='1' width='14' height='14' rx='3' fill='#1A1036' stroke='#BFB0F8' />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width='15'
    height='15'
    viewBox='0 0 24 24'
    fill='#FFD700'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M12 .587l3.668 7.431 8.2 1.192-5.934 5.787 1.402 8.15L12 18.896l-7.336 4.251 1.402-8.15L.132 9.21l8.2-1.192L12 .587z' />
  </svg>
);

const FilterSectionBlock: React.FC<{
  section: FilterSection;
  activeFilters: ActiveFilters;
  onToggleFilter: (section: FilterSectionId, id: string) => void;
}> = ({ section, activeFilters, onToggleFilter }) => (
  <div className='flex min-w-0 flex-col gap-3 md:min-w-[120px]'>
    <h4 className='mb-1 text-16'>{section.title}</h4>
    <div className='flex flex-col gap-2'>
      {section.options.map((option) => {
        const isChecked = activeFilters[section.id]?.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => onToggleFilter(section.id, option.id)}
            className={clsx(
              'group -ml-2 flex w-full items-center gap-2 rounded-[4px] px-2 py-1 transition-colors',
              'hover:bg-white/10'
            )}
            aria-pressed={isChecked}
          >
            {isChecked ? (
              <CheckedIcon className='size-[20px] shrink-0' />
            ) : (
              <UncheckedIcon className='size-[20px] shrink-0' />
            )}
            <div
              className={clsx(
                'flex min-w-0 items-center',
                option.hasStar ? 'flex-1 justify-between' : 'gap-2'
              )}
            >
              <span className='whitespace-nowrap text-left'>{option.label}</span>
              {option.hasStar && <StarIcon className='mb-0.5 size-[15px] shrink-0' />}
            </div>
          </button>
        );
      })}
    </div>
  </div>
);

/**
 * FilterPopup Component
 *
 * Displays a dropdown menu with categories, ratings, and popularity filters.
 * Uses Framer Motion for entrance/exit animations.
 */
export const FilterPopup: React.FC<FilterPopupProps> = ({
  isOpen,
  onClose,
  activeFilters,
  onToggleFilter,
  onClearAll,
  className,
  sections = DEFAULT_SECTIONS,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        className={clsx(
          'absolute z-50 flex flex-col',
          // Dimensions: Use token for width, auto for height to fit content
          'h-auto w-filter-popup-mobile md:w-fit',
          'rounded-20',
          // Padding: Mobile specific (9px top), Desktop standard (24px/p-6)
          'px-6 pb-6 pt-[9px] md:p-6',
          // Typography Base
          'font-sans text-14 font-medium leading-[16px] text-white',
          className
        )}
        style={{
          // Gradient Border Logic
          background:
            'linear-gradient(#1A1036, #1A1036) padding-box, linear-gradient(180deg, #ABF56E 0%, #BFB0F8 100%) border-box',
          border: '1px solid transparent',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
        }}
      >
        {/* Close Button Row */}
        <div className='flex w-full justify-end'>
          <button
            onClick={onClose}
            className='transition-opacity hover:opacity-80'
            aria-label='Close filters'
          >
            <img src={CloseIcon} alt='Close' className='size-auto' />
          </button>
        </div>

        {/* Two-column layout: Category | Rating + Popularity stacked */}
        <div className='flex gap-4'>
          {/* Column 1: Category */}
          <div className='min-w-0 shrink-0'>
            {sections.slice(0, 1).map((section) => (
              <FilterSectionBlock
                key={section.id}
                section={section}
                activeFilters={activeFilters}
                onToggleFilter={onToggleFilter}
              />
            ))}
          </div>

          {/* Column 2: Rating + Popularity (always stacked, fixed width) */}
          <div className='flex shrink-0 flex-col gap-6'>
            {sections.slice(1).map((section) => (
              <FilterSectionBlock
                key={section.id}
                section={section}
                activeFilters={activeFilters}
                onToggleFilter={onToggleFilter}
              />
            ))}
          </div>
        </div>

        {/* Bottom Row: Actions */}
        <div className='mt-2 flex justify-end'>
          <button
            onClick={onClearAll}
            className={clsx(
              'text-clear-action font-semibold',
              'text-gray-315 transition-colors hover:text-white',
              'underline decoration-solid'
            )}
          >
            Clear All
          </button>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default FilterPopup;
